import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { ContactModal, AddContactModal, photoSrc } from './CRM';
import './BDHub.css';

const TOKEN_KEY = 'intranet_token';
const authH = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// ── Relationship colors ───────────────────────────────────────────────────────
const REL_PALETTE = {
  'Client':           '#3b82f6',
  'Prospect':         '#f59e0b',
  'Opposing Counsel': '#ef4444',
  'Referral Source':  '#10b981',
  'Vendor':           '#8b5cf6',
  'Co-Counsel':       '#06b6d4',
  'Expert Witness':   '#f97316',
  'Other':            '#6b7280',
};
const AUTO_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1',
  '#14b8a6', '#f43f5e',
];
// Relationship-type badges all render in one neutral gray (no per-type colors).
const relColor = () => '#475569';

// ── SVG icons ─────────────────────────────────────────────────────────────────
const Ico = ({ d, children, ...p }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {children}
  </svg>
);
const IcoGrid    = () => <Ico><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></Ico>;
const IcoUsers   = () => <Ico><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>;
const IcoSearch  = () => <Ico><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico>;
const IcoStack   = () => <Ico><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></Ico>;
const IcoBuilding = () => <Ico><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></Ico>;
const IcoLog     = () => <Ico><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3 3"/></Ico>;

// Selectable icons for marketing lists (keyed; stored on crm_lists.icon).
const LIST_ICONS = {
  stack:     <Ico><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></Ico>,
  wine:      <Ico><path d="M8 22h8"/><path d="M12 15v7"/><path d="M7 3h10l-1 7a4 4 0 0 1-8 0z"/></Ico>,
  gift:      <Ico><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><line x1="12" y1="8" x2="12" y2="21"/><path d="M12 8H7.5a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8z"/><path d="M12 8h4.5a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8z"/></Ico>,
  star:      <Ico><polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3 12 2"/></Ico>,
  building:  <Ico><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></Ico>,
  scales:    <Ico><path d="M12 3v18"/><path d="M5 7h14"/><path d="M7 7l-3 6a3 3 0 0 0 6 0z"/><path d="M17 7l-3 6a3 3 0 0 0 6 0z"/><path d="M8 21h8"/></Ico>,
  briefcase: <Ico><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></Ico>,
  users:     <Ico><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>,
  mail:      <Ico><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Ico>,
  calendar:  <Ico><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ico>,
  heart:     <Ico><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></Ico>,
  flag:      <Ico><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></Ico>,
};
const LIST_ICON_KEYS = Object.keys(LIST_ICONS);
const listIcon = k => LIST_ICONS[k] || LIST_ICONS.stack;
const LIST_COLORS = ['#7c1d3f', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#db2777', '#0d9488', '#475569'];

const TABS = [
  { id: 'contacts',  label: 'Contacts',           icon: <IcoUsers /> },
  { id: 'companies', label: 'Companies',          icon: <IcoBuilding /> },
  { id: 'lists',     label: 'Marketing Lists',    icon: <IcoStack /> },
  { id: 'dashboard', label: 'Dashboard',          icon: <IcoGrid /> },
  { id: 'logs',      label: 'Logs (Admin only)',  icon: <IcoLog /> },
];

// ── Interactive growth chart ──────────────────────────────────────────────────
const GROWTH_TYPES = ['Area', 'Line', 'Bar'];

const GrowthChart = ({ data }) => {
  const [type, setType] = useState('Area');
  const [hov, setHov]   = useState(null);
  const W = 340, H = 94;
  const n = data.length;
  const max = Math.max(...data.map(d => d.count), 1);

  const pts = data.map((d, i) => ({
    x: 16 + (i / Math.max(n - 1, 1)) * (W - 32),
    y: H - 16 - (d.count / max) * (H - 30),
    ...d,
  }));
  const bW = (W - 32) / n - 5;
  const bX = i => 16 + i * ((W - 32) / n) + 2;
  const line = pts.map(p => `${p.x},${p.y}`).join(' ');
  const area = `${pts[0].x},${H} ${line} ${pts[n - 1].x},${H}`;
  const tipX = hov === null ? 0
    : type === 'Bar' ? (bX(hov) + bW / 2) / W * 100
    : pts[hov].x / W * 100;

  return (
    <div className="bd-ichart">
      <div className="bd-chart-pills">
        {GROWTH_TYPES.map(t => (
          <button key={t} className={`bd-cpill${type === t ? ' bd-cpill--on' : ''}`} onClick={() => setType(t)}>{t}</button>
        ))}
      </div>
      <div className="bd-ichart-body" onMouseLeave={() => setHov(null)}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="bd-spark-svg">
          <defs>
            <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {type === 'Bar' ? data.map((d, i) => {
            const bh = Math.max((d.count / max) * (H - 20), 2);
            return (
              <g key={i} onMouseEnter={() => setHov(i)}>
                <rect x={bX(i)} y={H - bh - 4} width={bW} height={bh}
                  fill={hov === i ? '#2563eb' : '#3b82f6'} rx="4"
                  opacity={hov !== null && hov !== i ? 0.3 : 1}
                  style={{ transition: 'opacity 0.12s, fill 0.1s' }} />
                <rect x={bX(i)} y={0} width={bW} height={H} fill="transparent" />
              </g>
            );
          }) : (
            <>
              {type === 'Area' && <polygon points={area} fill="url(#sg)" />}
              <polyline points={line} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              {pts.map((p, i) => (
                <g key={i} onMouseEnter={() => setHov(i)}>
                  <circle cx={p.x} cy={p.y} r={hov === i ? 5.5 : i === n - 1 ? 4 : 3}
                    fill={hov === i || i === n - 1 ? '#2563eb' : '#3b82f6'}
                    stroke="#ffffff" strokeWidth="1.5" />
                  <rect x={p.x - 20} y={0} width={40} height={H} fill="transparent" />
                </g>
              ))}
            </>
          )}
        </svg>
        {hov !== null && (
          <div className="bd-chart-tip" style={{ left: `${tipX}%` }}>
            <span>{data[hov].label}</span>
            <strong>{data[hov].count} added</strong>
          </div>
        )}
      </div>
      <div className="bd-spark-labels">
        {data.map(m => <span key={m.label}>{m.label}</span>)}
      </div>
    </div>
  );
};

// ── Interactive relationship chart ───────────────────────────────────────────
const REL_TYPES = ['Donut', 'Pie', 'Bar'];

const RelChart = ({ segments, totalContacts, onClickSegment }) => {
  const [type, setType] = useState('Donut');
  const [hov, setHov]   = useState(null);
  if (!segments.length) return <p className="bd-empty-sub">No relationship data yet.</p>;

  const total  = segments.reduce((s, d) => s + d.value, 0);
  const hovSeg = hov !== null ? segments[hov] : null;
  const maxVal = segments[0]?.value || 1;
  const size = 160, cx = 80, cy = 80;

  // Donut arcs
  const rD = 58, circD = 2 * Math.PI * rD;
  let offD = 0;
  const donutArcs = segments.map(seg => {
    const dash = (seg.value / total) * circD;
    const a = { ...seg, off: offD, dash };
    offD += dash;
    return a;
  });

  // Pie paths
  const rP = 74;
  let ang = -Math.PI / 2;
  const pieArcs = segments.map(seg => {
    const theta = (seg.value / total) * 2 * Math.PI;
    const s = ang, mid = ang + theta / 2, e = ang + theta;
    ang += theta;
    const lg = theta > Math.PI ? 1 : 0;
    const x1 = cx + rP * Math.cos(s), y1 = cy + rP * Math.sin(s);
    const x2 = cx + rP * Math.cos(e), y2 = cy + rP * Math.sin(e);
    return { ...seg, mid, d: `M ${cx} ${cy} L ${x1} ${y1} A ${rP} ${rP} 0 ${lg} 1 ${x2} ${y2} Z` };
  });

  return (
    <div className="bd-ichart">
      <div className="bd-chart-pills">
        {REL_TYPES.map(t => (
          <button key={t} className={`bd-cpill${type === t ? ' bd-cpill--on' : ''}`} onClick={() => setType(t)}>{t}</button>
        ))}
      </div>

      {type === 'Bar' ? (
        <div className="bd-rel-bars">
          {segments.map((s, i) => (
            <div key={s.name} className="bd-rel-bar-row"
              onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}
              onClick={() => onClickSegment?.(s.name)}
              style={{ opacity: hov !== null && hov !== i ? 0.38 : 1, transition: 'opacity 0.12s', cursor: 'pointer' }}>
              <span className="bd-rel-bar-nm">{s.name}</span>
              <div className="bd-rel-bar-track">
                <div className="bd-rel-bar-fill" style={{ width: `${(s.value / maxVal) * 100}%`, background: s.color }} />
              </div>
              <span className="bd-rel-bar-n">{s.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bd-donut-layout">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="bd-donut-svg"
            onMouseLeave={() => setHov(null)}>
            {type === 'Donut' && (
              <>
                <circle cx={cx} cy={cy} r={rD} fill="none" stroke="#f1f5f9" strokeWidth="26" />
                {donutArcs.map((arc, i) => (
                  <circle key={i} cx={cx} cy={cy} r={rD} fill="none"
                    stroke={arc.color}
                    strokeWidth={hov === i ? 28 : 22}
                    strokeDasharray={`${arc.dash} ${circD}`}
                    strokeDashoffset={-arc.off}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${cx} ${cy})`}
                    opacity={hov !== null && hov !== i ? 0.3 : 1}
                    style={{ cursor: 'pointer', transition: 'opacity 0.12s, stroke-width 0.15s' }}
                    onMouseEnter={() => setHov(i)}
                    onClick={() => onClickSegment?.(arc.name)}
                  />
                ))}
                <text x={cx} y={cy - 6} textAnchor="middle" fill="#1e2a3a" fontSize="21" fontWeight="700" fontFamily="Inter, system-ui, sans-serif">
                  {hovSeg ? hovSeg.value : (totalContacts ?? total).toLocaleString()}
                </text>
                <text x={cx} y={cy + 11} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter, system-ui, sans-serif">
                  {hovSeg ? `${Math.round((hovSeg.value / total) * 100)}%` : 'contacts'}
                </text>
                {hovSeg && (
                  <text x={cx} y={cy + 25} textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter, system-ui, sans-serif">
                    {hovSeg.name}
                  </text>
                )}
              </>
            )}
            {type === 'Pie' && pieArcs.map((arc, i) => (
              <path key={i} d={arc.d} fill={arc.color}
                stroke="#ffffff" strokeWidth="2"
                opacity={hov !== null && hov !== i ? 0.3 : 1}
                transform={hov === i ? `translate(${+(Math.cos(arc.mid)*7).toFixed(2)} ${+(Math.sin(arc.mid)*7).toFixed(2)})` : ''}
                style={{ cursor: 'pointer', transition: 'opacity 0.12s' }}
                onMouseEnter={() => setHov(i)}
                onClick={() => onClickSegment?.(arc.name)}
              />
            ))}
          </svg>
          <div className="bd-donut-legend">
            {segments.map((s, i) => (
              <div key={s.name} className={`bd-legend-row${hov === i ? ' bd-legend-row--hov' : ''}`}
                onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}
                onClick={() => onClickSegment?.(s.name)}
                style={{ cursor: 'pointer', transition: 'opacity 0.12s', opacity: hov !== null && hov !== i ? 0.45 : 1 }}>
                <span className="bd-legend-dot" style={{ background: s.color }} />
                <span className="bd-legend-name">{s.name}</span>
                <span className="bd-legend-count">{s.value}</span>
              </div>
            ))}
            {hovSeg && type === 'Pie' && (
              <div className="bd-pie-info">
                <strong>{hovSeg.name}</strong>
                <span>{hovSeg.value} · {Math.round((hovSeg.value / total) * 100)}%</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = ({ contacts, loading, onNavigate }) => {
  const stats = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { label: d.toLocaleString('default', { month: 'short' }), year: d.getFullYear(), month: d.getMonth(), count: 0 };
    });
    contacts.forEach(c => {
      if (!c.created_at) return;
      const d = new Date(c.created_at);
      const idx = months.findIndex(m => m.month === d.getMonth() && m.year === d.getFullYear());
      if (idx !== -1) months[idx].count++;
    });
    const byRel = {};
    contacts.forEach(c => { const r = c.relationship_type || 'Unspecified'; byRel[r] = (byRel[r] || 0) + 1; });
    const byCompany = {};
    contacts.forEach(c => { if (c.company) byCompany[c.company] = (byCompany[c.company] || 0) + 1; });
    const topCos = Object.entries(byCompany).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const companies = new Set(contacts.map(c => c.company).filter(Boolean)).size;
    const thisMonth = contacts.filter(c => {
      if (!c.created_at) return false;
      const d = new Date(c.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const recent = [...contacts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 7);
    const donut = Object.entries(byRel)
      .sort((a, b) => b[1] - a[1]).slice(0, 7)
      .map(([name, value], i) => ({ name, value, color: REL_PALETTE[name] || AUTO_COLORS[i % AUTO_COLORS.length] }));
    return { months, byRel, topCos, companies, thisMonth, recent, donut };
  }, [contacts]);

  const maxCo = stats.topCos[0]?.[1] || 1;

  if (loading) return (
    <div className="bd-loading">
      <div className="bd-spinner" />
      <p>Loading analytics…</p>
    </div>
  );

  return (
    <div className="bd-dashboard">
      <div className="bd-stat-row">
        {/* Total Contacts */}
        <div className="bd-stat-card" style={{ '--ac': '#3b82f6', '--glow': '#3b82f633', cursor: 'pointer' }}
          onClick={() => onNavigate('contacts')}>
          <p className="bd-stat-val">{contacts.length.toLocaleString()}</p>
          <p className="bd-stat-lbl">Total Contacts</p>
          <div className="bd-stat-bar" />
        </div>
        {/* Companies */}
        <div className="bd-stat-card" style={{ '--ac': '#22d3ee', '--glow': '#22d3ee33', cursor: 'pointer' }}
          onClick={() => onNavigate('companies')}>
          <p className="bd-stat-val">{stats.companies.toLocaleString()}</p>
          <p className="bd-stat-lbl">Companies</p>
          <div className="bd-stat-bar" />
        </div>
        {/* Added This Month */}
        <div className="bd-stat-card" style={{ '--ac': '#34d399', '--glow': '#34d39933', cursor: 'pointer' }}
          onClick={() => onNavigate('contacts')}>
          <p className="bd-stat-val">{stats.thisMonth}</p>
          <p className="bd-stat-lbl">Added This Month</p>
          <div className="bd-stat-bar" />
        </div>
      </div>

      <div className="bd-bento">
        {/* Trend */}
        <div className="bd-bento-card bd-bento--trend">
          <h3 className="bd-card-title">Contact Growth <span className="bd-card-sub">last 6 months</span></h3>
          <GrowthChart data={stats.months} />
        </div>

        {/* Relationship */}
        <div className="bd-bento-card bd-bento--donut">
          <h3 className="bd-card-title">By Relationship</h3>
          <RelChart segments={stats.donut} totalContacts={contacts.length}
            onClickSegment={name => onNavigate('contacts', { rel: name })} />
        </div>

        {/* Top companies */}
        <div className="bd-bento-card bd-bento--companies">
          <h3 className="bd-card-title">Top Companies</h3>
          <div className="bd-bar-list">
            {stats.topCos.map(([name, count], i) => (
              <div key={name} className="bd-bar-row bd-bar-row--click"
                onClick={() => onNavigate('companies', { company: name })}>
                <span className="bd-bar-rank">{i + 1}</span>
                <span className="bd-bar-name">{name}</span>
                <div className="bd-bar-track">
                  <div className="bd-bar-fill" style={{ width: `${(count / maxCo) * 100}%` }} />
                </div>
                <span className="bd-bar-n">{count}</span>
              </div>
            ))}
            {stats.topCos.length === 0 && <p className="bd-empty-sub">No company data yet.</p>}
          </div>
        </div>

        {/* Recent */}
        <div className="bd-bento-card bd-bento--recent">
          <h3 className="bd-card-title">Recently Added</h3>
          <div className="bd-recent-list">
            {stats.recent.map(c => (
              <div key={c.id} className="bd-recent-row bd-recent-row--click"
                onClick={() => onNavigate('contacts', { contactId: c.id })}>
                <div className="bd-av">
                  {(c.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  {photoSrc(c) && <img src={photoSrc(c)} alt="" className="bd-av-photo" onError={e => { e.target.style.display = 'none'; }} />}
                </div>
                <div className="bd-recent-info">
                  <span className="bd-recent-name">{c.name || '—'}</span>
                  <span className="bd-recent-co">{c.company || c.position || '—'}</span>
                </div>
                {c.relationship_type && (
                  <span className="bd-rel-pip" style={{ background: relColor(c.relationship_type) + '33', color: relColor(c.relationship_type), borderColor: relColor(c.relationship_type) + '55' }}>
                    {c.relationship_type}
                  </span>
                )}
              </div>
            ))}
            {stats.recent.length === 0 && <p className="bd-empty-sub">No contacts yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Import modal ──────────────────────────────────────────────────────────────
const IMPORT_FIELDS = [
  { key: 'name',             label: 'Full Name' },
  { key: 'first_name',       label: 'First Name' },
  { key: 'last_name',        label: 'Last Name' },
  { key: 'work_email',       label: 'Work Email' },
  { key: 'personal_email',   label: 'Personal Email' },
  { key: 'company',          label: 'Company' },
  { key: 'position',         label: 'Job Title' },
  { key: 'work_phone',       label: 'Work Phone' },
  { key: 'personal_phone',   label: 'Mobile / Personal' },
  { key: 'work_address',      label: 'Work Address' },
  { key: 'personal_address',  label: 'Personal Address' },
  { key: 'relationship_type', label: 'Relationship Type' },
  { key: 'industry',          label: 'Industry' },
  { key: 'wine_tier',         label: 'Wine (1–4)' },
];

const ImportModal = ({ attorneys, currentUser, onClose, onImported }) => {
  const isAdmin = currentUser?.isAdmin;
  const isChris = currentUser?.name === 'Christopher J. Esbrook';
  const [step, setStep]           = useState('upload');
  const [fileKey, setFileKey]     = useState(0);
  const [file, setFile]           = useState(null);
  const [parsing, setParsing]     = useState(false);
  const [parsed, setParsed]       = useState(null);
  const [mapping, setMapping]     = useState({});
  const [attorneyId, setAttyId]   = useState('');
  const [importing, setImporting] = useState(false);
  const [imported, setImported]   = useState(0);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [wineList, setWineList]   = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setParsing(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post('/api/crm/contacts/parse', fd, authH());
      setParsed(data);
      const { attorney: _a, ...suggested } = data.suggested || {};
      // Prefer split first+last over a full-name column, never map both
      if (suggested.first_name || suggested.last_name) {
        delete suggested.name;
      } else if (suggested.name) {
        delete suggested.first_name;
        delete suggested.last_name;
      }
      setMapping(suggested);
      setStep('map');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not parse file');
    } finally {
      setParsing(false);
      setFileKey(k => k + 1);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping', JSON.stringify(mapping));
      if (attorneyId) fd.append('attorney_id', attorneyId);
      if (wineList) fd.append('wine_list', 'true');
      const { data } = await axios.post('/api/crm/contacts/import', fd, authH());
      setImported(data.imported);
      setResult(data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => { setStep('upload'); setFileKey(k => k + 1); setError(''); setParsed(null); };

  return (
    <div className="bd-modal-back" onClick={step === 'upload' ? onClose : undefined}>
      <div className="bd-import-box" onClick={e => e.stopPropagation()}>

        <div className="bd-import-head">
          <h2 className="bd-import-title">
            {step === 'upload' && 'Import Contacts'}
            {step === 'map'    && 'Map Columns'}
            {step === 'done'   && 'Import Complete'}
          </h2>
          <button className="bd-import-x" onClick={onClose}>✕</button>
        </div>

        {step === 'upload' && (
          <div className="bd-import-body">
            <label className="bd-drop-zone">
              <input key={fileKey} type="file" accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }} onChange={handleFile} />
              {parsing ? (
                <div className="bd-import-spin"><div className="bd-spinner" /><span>Parsing file…</span></div>
              ) : (
                <>
                  <div className="bd-drop-ico">⬆</div>
                  <p className="bd-drop-main">Drop a CSV or XLSX file here</p>
                  <p className="bd-drop-sub">or click to browse</p>
                </>
              )}
            </label>
            {error && <p className="bd-import-err">{error}</p>}
          </div>
        )}

        {step === 'map' && parsed && (
          <>
            <div className="bd-import-filemeta">
              <span className="bd-import-fname">{file?.name}</span>
              <span className="bd-import-fcount">{parsed.total.toLocaleString()} rows detected</span>
            </div>
            <div className="bd-import-body bd-import-body--map">
              <div className="bd-map-section">
                <p className="bd-map-hd">Column Mapping</p>
                <div className="bd-map-grid">
                  {IMPORT_FIELDS.map(f => (
                    <React.Fragment key={f.key}>
                      <label className="bd-map-lbl">{f.label}</label>
                      <select className="bd-sel bd-map-sel"
                        value={mapping[f.key] || ''}
                        onChange={e => setMapping(m => {
                          const n = { ...m };
                          if (e.target.value) {
                            n[f.key] = e.target.value;
                            // Mutual exclusivity: mapping name clears first/last, and vice versa
                            if (f.key === 'name') { delete n.first_name; delete n.last_name; }
                            if (f.key === 'first_name' || f.key === 'last_name') delete n.name;
                          } else {
                            delete n[f.key];
                          }
                          return n;
                        })}>
                        <option value="">— skip —</option>
                        {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </React.Fragment>
                  ))}
                </div>
                {(mapping.first_name || mapping.last_name) && (
                  <p className="bd-map-hint">
                    First + Last will be combined into a full name automatically
                  </p>
                )}
              </div>

              {isAdmin && (
                <div className="bd-map-section">
                  <p className="bd-map-hd">Assign to Attorney</p>
                  <select className="bd-sel" style={{ width: '100%' }} value={attorneyId} onChange={e => setAttyId(e.target.value)}>
                    <option value="">— me (default) —</option>
                    {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}

              {(isChris || isAdmin) && (
                <div className="bd-map-section">
                  <label className="bd-winelist-toggle">
                    <input type="checkbox" checked={wineList} onChange={e => setWineList(e.target.checked)} />
                    <span className="bd-winelist-box" aria-hidden="true" />
                    <span className="bd-winelist-copy">
                      <span className="bd-winelist-title">🍷 This is Chris' Wine List</span>
                      <span className="bd-winelist-sub">Tags every imported contact <b>Chris' Wine List</b> and adds them all to the <b>Chris' Wine List</b> marketing list.</span>
                    </span>
                  </label>
                </div>
              )}

              {parsed.preview.length > 0 && (
                <div className="bd-map-section">
                  <p className="bd-map-hd">
                    Preview <span className="bd-card-sub">first {parsed.preview.length} rows</span>
                  </p>
                  <div className="bd-import-preview-wrap">
                    <table className="bd-import-preview">
                      <thead>
                        <tr>{parsed.headers.map(h => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {parsed.preview.map((row, i) => (
                          <tr key={i}>
                            {parsed.headers.map(h => <td key={h}>{String(row[h] ?? '')}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {error && <p className="bd-import-err">{error}</p>}
            </div>
            <div className="bd-import-foot">
              <button className="bd-import-back" onClick={reset}>← Back</button>
              <button className="bd-import-go" onClick={handleImport} disabled={importing}>
                {importing ? 'Importing…' : `Import ${parsed.total.toLocaleString()} contacts →`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <div className="bd-import-body bd-import-done">
              <div className="bd-done-ico">✓</div>
              <p className="bd-done-msg">
                {imported.toLocaleString()} row{imported !== 1 ? 's' : ''} processed
              </p>
              {result && (
                <p className="bd-done-sub">
                  {(result.created ?? 0).toLocaleString()} new contact{result.created !== 1 ? 's' : ''} added
                  {result.merged > 0 && <> · {result.merged.toLocaleString()} matched an existing person (connection added)</>}
                </p>
              )}
              {result?.wineListAdded > 0 && (
                <p className="bd-done-sub">🍷 {result.wineListAdded.toLocaleString()} added to <b>Chris' Wine List</b> (tagged + on the marketing list)</p>
              )}
            </div>
            <div className="bd-import-foot">
              <span />
              <button className="bd-import-go" onClick={() => { onImported(); onClose(); }}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

const SORT_OPTIONS = [
  { val: 'name_asc',        label: 'Name A → Z',     col: 'name',       dir: 'asc'  },
  { val: 'name_desc',       label: 'Name Z → A',     col: 'name',       dir: 'desc' },
  { val: 'company_asc',     label: 'Company A → Z',  col: 'company',    dir: 'asc'  },
  { val: 'company_desc',    label: 'Company Z → A',  col: 'company',    dir: 'desc' },
  { val: 'position_asc',    label: 'Title A → Z',    col: 'position',   dir: 'asc'  },
  { val: 'created_at_desc', label: 'Newest Added',   col: 'created_at', dir: 'desc' },
  { val: 'created_at_asc',  label: 'Oldest Added',   col: 'created_at', dir: 'asc'  },
];

// Default wine legend (the 1–4 bottles). A wine list can override these per-list
// via crm_lists.wine_selections; this is the fallback when it hasn't.
const WINE_INFO = {
  1: { vintage: '2022', name: 'Shafer Napa Valley Cabernet',            price: '$89' },
  2: { vintage: '2020', name: 'Château Pichon Baron “Griffons de Pichon”', price: '$49' },
  3: { vintage: '2022', name: 'Turnbull Napa Valley Cabernet',          price: '$38' },
  4: { vintage: '2022', name: 'Chiarlo Cipressi Barbera Nizza',         price: '$21' },
};
// Single-line hover label for a wine number, from a given wine set.
const wineLabel = (wines, n) => (wines[n]
  ? `${n} — ${[wines[n].vintage, wines[n].name].filter(Boolean).join(' ')}${wines[n].price ? ` (${wines[n].price})` : ''}`
  : String(n));

const CONTACT_COLUMNS = [
  { key: 'name',        label: 'Name', always: true },
  { key: 'position',    label: 'Title' },
  { key: 'company',     label: 'Company' },
  { key: 'industry',    label: 'Industry' },
  { key: 'connections', label: 'Attorney Connections' },
  { key: 'email',       label: 'Email' },
];
const DEFAULT_COLS = ['name', 'position', 'company', 'industry', 'connections', 'email'];

// Show-first-N control (50 / 100 / All) shown at the bottom of a list
const PageSizeBar = ({ pageSize, setPageSize, shown, total, noun = 'items' }) => {
  if (!total) return null;
  return (
    <div className="bd-pagesize">
      <span className="bd-pagesize-info">Showing {shown.toLocaleString()} of {total.toLocaleString()} {noun}</span>
      <div className="bd-pagesize-btns">
        <span className="bd-pagesize-label">Show</span>
        {[50, 100, 'all'].map(sz => (
          <button key={sz} className={`bd-pagesize-btn${pageSize === sz ? ' on' : ''}`} onClick={() => setPageSize(sz)}>
            {sz === 'all' ? 'All' : sz}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Contacts tab ──────────────────────────────────────────────────────────────
const ContactsView = ({ currentUser, contacts, tags, attorneys, loading, onRefresh, onTagCreated, navCtx = {} }) => {
  const isAdmin = currentUser?.isAdmin;
  const [search, setSearch]         = useState('');
  const [pageSize, setPageSize]     = useState(50);
  const [filterCos, setFilterCos]   = useState([]);
  const [filterAttys, setFilterAttys] = useState([]);
  const [filterRels, setFilterRels] = useState(navCtx.rel ? [navCtx.rel] : []);
  const [sortVal, setSortVal]       = useState('name_asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [openFilterSec, setOpenFilterSec] = useState(null); // 'atty' | 'rel' | 'company'
  const [coSearch, setCoSearch]     = useState('');
  const [attySearch, setAttySearch] = useState('');
  const [relSearch, setRelSearch]   = useState('');
  const filterPanelRef              = useRef(null);
  const [colsOpen, setColsOpen]     = useState(false);
  const colsRef                     = useRef(null);
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('bd_contacts_cols')); if (Array.isArray(s) && s.length) return new Set([...s, 'name']); } catch {}
    return new Set(DEFAULT_COLS);
  });
  const showCol = k => visibleCols.has(k);
  const toggleCol = k => setVisibleCols(s => {
    const n = new Set(s);
    n.has(k) ? n.delete(k) : n.add(k);
    n.add('name');
    try { localStorage.setItem('bd_contacts_cols', JSON.stringify([...n])); } catch {}
    return n;
  });
  const [openContact, setOpen]      = useState(() =>
    navCtx.contactId ? (contacts.find(c => c.id === navCtx.contactId) || null) : null
  );
  const [showAdd, setShowAdd]       = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected]     = useState(new Set());
  const [deleting, setDeleting]     = useState(false);
  const [bulkType, setBulkType]     = useState('');
  const [settingType, setSettingType] = useState(false);
  const selectAllRef                = useRef(null);

  const companies = useMemo(() =>
    [...new Set(contacts.map(c => c.company).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
  [contacts]);

  const relTypes = useMemo(() =>
    [...new Set(contacts.flatMap(c => (c.connections || []).map(cn => cn.relationship_type)).filter(Boolean))].sort(),
  [contacts]);

  const { col: sortCol, dir: sortDir } = useMemo(() =>
    SORT_OPTIONS.find(o => o.val === sortVal) || SORT_OPTIONS[0], [sortVal]);

  const sort = (col) => setSortVal(v => {
    const cur = SORT_OPTIONS.find(o => o.val === v) || SORT_OPTIONS[0];
    const wantDir = cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc';
    const opt = SORT_OPTIONS.find(o => o.col === col && o.dir === wantDir);
    return opt ? opt.val : v;
  });


  const filtered = useMemo(() => {
    let out = [...contacts];
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.work_email || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.position || '').toLowerCase().includes(q)
      );
    }
    if (filterCos.length)   out = out.filter(c => filterCos.includes(c.company));
    // Attorney / relationship filters match ANY of the contact's connections,
    // so filtering by an attorney surfaces everyone they're connected to.
    if (filterAttys.length) out = out.filter(c => (c.connections || []).some(cn => filterAttys.includes(String(cn.attorney_id))));
    if (filterRels.length)  out = out.filter(c => (c.connections || []).some(cn => filterRels.includes(cn.relationship_type)));
    out.sort((a, b) => {
      let av = a[sortCol] || '', bv = b[sortCol] || '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      return sortDir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
    });
    return out;
  }, [contacts, search, filterCos, filterAttys, filterRels, sortCol, sortDir]);

  const SortArr = ({ col }) => (
    <span className={`bd-th-arr${sortCol === col ? ' on' : ''}`}>
      {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const toggleCo   = v => setFilterCos(p  => p.includes(v)  ? p.filter(x => x !== v)  : [...p, v]);
  const toggleAtty = v => setFilterAttys(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const toggleRel  = v => setFilterRels(p  => p.includes(v)  ? p.filter(x => x !== v)  : [...p, v]);
  const activeFilterCount = filterCos.length + filterAttys.length + filterRels.length;
  const clearFilters = () => { setFilterCos([]); setFilterAttys([]); setFilterRels([]); };

  // Close filter panel on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = e => { if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  // Close columns dropdown on outside click
  useEffect(() => {
    if (!colsOpen) return;
    const handler = e => { if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colsOpen]);


  // How many rows to show (50 / 100 / All)
  const displayed = pageSize === 'all' ? filtered : filtered.slice(0, pageSize);

  const selInFiltered = displayed.filter(c => selected.has(c.id)).length;
  const allSelected   = displayed.length > 0 && selInFiltered === displayed.length;
  const indeterminate = selInFiltered > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => { const n = new Set(prev); displayed.forEach(c => n.delete(c.id)); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); displayed.forEach(c => n.add(c.id)); return n; });
    }
  };

  const toggleOne = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} contact${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete('/api/crm/contacts/bulk', { ...authH(), data: { ids } });
      setSelected(new Set());
      onRefresh();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const bulkSetType = async () => {
    if (!bulkType) return;
    const ids = [...selected];
    setSettingType(true);
    try {
      await axios.patch('/api/crm/contacts/bulk', { ids, relationship_type: bulkType }, authH());
      setBulkType('');
      setSelected(new Set());
      onRefresh();
    } catch (err) {
      alert('Update failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSettingType(false);
    }
  };

  return (
    <div className="bd-contacts">
      <div className="bd-contacts-bar">
        {/* Grey pill search */}
        <div className="bd-search-pill">
          <IcoSearch />
          <input className="bd-search" placeholder="Search name, email, company…"
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="bd-search-x" onClick={() => setSearch('')}>✕</button>}
        </div>

        {/* Filter By — nested dropdowns per category, each with its own search */}
        <div className="bd-filter-wrap" ref={filterPanelRef}>
          <button className={`bd-ctrl-drop bd-filter-btn${filterOpen ? ' open' : ''}`} onClick={() => setFilterOpen(o => !o)}>
            Filter By
            {activeFilterCount > 0 && <span className="bd-filter-badge">{activeFilterCount}</span>}
            <span className="bd-filter-caret">▾</span>
          </button>
          {filterOpen && (
            <div className="bd-filter-panel">
              {/* Attorney filter */}
              <div className="bd-fp-acc">
                <button className="bd-fp-acc-hd" onClick={() => setOpenFilterSec(s => s === 'atty' ? null : 'atty')}>
                  <span>Attorney</span>
                  {filterAttys.length > 0 && <span className="bd-fp-acc-badge">{filterAttys.length}</span>}
                  <span className="bd-fp-acc-caret">{openFilterSec === 'atty' ? '▴' : '▾'}</span>
                </button>
                {openFilterSec === 'atty' && (
                  <div className="bd-fp-acc-body">
                    <input className="bd-fp-search" placeholder="Search attorneys…" value={attySearch} onChange={e => setAttySearch(e.target.value)} />
                    <div className="bd-fp-scroll">
                      {attorneys.filter(a => a.name.toLowerCase().includes(attySearch.toLowerCase())).map(a => (
                        <label key={a.id} className="bd-fp-row">
                          <input type="checkbox" checked={filterAttys.includes(String(a.id))} onChange={() => toggleAtty(String(a.id))} />
                          <span>{a.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Relationship Type */}
              <div className="bd-fp-acc">
                <button className="bd-fp-acc-hd" onClick={() => setOpenFilterSec(s => s === 'rel' ? null : 'rel')}>
                  <span>Relationship Type</span>
                  {filterRels.length > 0 && <span className="bd-fp-acc-badge">{filterRels.length}</span>}
                  <span className="bd-fp-acc-caret">{openFilterSec === 'rel' ? '▴' : '▾'}</span>
                </button>
                {openFilterSec === 'rel' && (
                  <div className="bd-fp-acc-body">
                    <input className="bd-fp-search" placeholder="Search relationship types…" value={relSearch} onChange={e => setRelSearch(e.target.value)} />
                    <div className="bd-fp-scroll">
                      {relTypes.filter(r => r.toLowerCase().includes(relSearch.toLowerCase())).map(r => (
                        <label key={r} className="bd-fp-row">
                          <input type="checkbox" checked={filterRels.includes(r)} onChange={() => toggleRel(r)} />
                          <span>{r}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Company */}
              <div className="bd-fp-acc">
                <button className="bd-fp-acc-hd" onClick={() => setOpenFilterSec(s => s === 'company' ? null : 'company')}>
                  <span>Company</span>
                  {filterCos.length > 0 && <span className="bd-fp-acc-badge">{filterCos.length}</span>}
                  <span className="bd-fp-acc-caret">{openFilterSec === 'company' ? '▴' : '▾'}</span>
                </button>
                {openFilterSec === 'company' && (
                  <div className="bd-fp-acc-body">
                    <input className="bd-fp-search" placeholder="Search companies…" value={coSearch} onChange={e => setCoSearch(e.target.value)} />
                    <div className="bd-fp-scroll">
                      {companies.filter(c => c.toLowerCase().includes(coSearch.toLowerCase())).slice(0, 80).map(c => (
                        <label key={c} className="bd-fp-row">
                          <input type="checkbox" checked={filterCos.includes(c)} onChange={() => toggleCo(c)} />
                          <span>{c}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {activeFilterCount > 0 && (
                <button className="bd-fp-clear" onClick={clearFilters}>Clear all filters</button>
              )}
            </div>
          )}
        </div>

        {/* Columns */}
        <div className="bd-filter-wrap" ref={colsRef}>
          <button className={`bd-ctrl-drop bd-filter-btn${colsOpen ? ' open' : ''}`} onClick={() => setColsOpen(o => !o)}>
            Columns <span className="bd-filter-caret">▾</span>
          </button>
          {colsOpen && (
            <div className="bd-filter-panel bd-cols-panel">
              <p className="bd-fp-hd">Show columns</p>
              {CONTACT_COLUMNS.map(col => (
                <label key={col.key} className={`bd-fp-row${col.always ? ' bd-fp-row--dis' : ''}`}>
                  <input type="checkbox" checked={showCol(col.key)} disabled={col.always} onChange={() => toggleCol(col.key)} />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="bd-bar-actions">
          <button className="bd-ctrl-btn bd-export-btn" onClick={() => {
            const CSV_COLS = [
              { key: 'name',              label: 'Name' },
              { key: 'company',           label: 'Company' },
              { key: 'position',          label: 'Title' },
              { key: 'industry',          label: 'Industry' },
              { label: 'Attorneys',     val: c => (c.connections || []).map(cn => cn.attorney_name).filter(Boolean).join('; ') },
              { label: 'Relationships', val: c => (c.connections || []).map(cn => cn.relationship_type).filter(Boolean).join('; ') },
              { key: 'work_email',        label: 'Work Email' },
              { key: 'personal_email',    label: 'Personal Email' },
              { key: 'work_phone',        label: 'Work Phone' },
              { key: 'linkedin_url',      label: 'LinkedIn' },
            ];
            const esc = v => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
            const rows = [
              CSV_COLS.map(c => esc(c.label)).join(','),
              ...filtered.map(c => CSV_COLS.map(col => esc(col.val ? col.val(c) : c[col.key])).join(',')),
            ];
            const blob = new Blob([rows.join('\r\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'contacts.csv'; a.click();
            URL.revokeObjectURL(url);
          }}>↓ Export</button>
          <button className="bd-ctrl-btn bd-import-btn" onClick={() => setShowImport(true)}>↑ Import</button>
          <button className="bd-ctrl-btn" onClick={() => setShowAdd(true)}>+ Add Contact</button>
        </div>
      </div>

      <div className="bd-table-wrap">
        {loading ? (
          <div className="bd-loading"><div className="bd-spinner" /></div>
        ) : (
          <>
            <div className="bd-table-top">
              <p className="bd-table-meta">
                {filtered.length.toLocaleString()} of {contacts.length.toLocaleString()} contacts
              </p>
              {selected.size > 0 && (
                <div className="bd-bulk-acts">
                  <span className="bd-bulk-count">{selected.size} selected</span>
                  <select className="bd-sel bd-bulk-type-sel" value={bulkType}
                    onChange={e => setBulkType(e.target.value)}>
                    <option value="">Set type…</option>
                    {Object.keys(REL_PALETTE).map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {bulkType && (
                    <button className="bd-bulk-apply" onClick={bulkSetType} disabled={settingType}>
                      {settingType ? 'Applying…' : 'Apply'}
                    </button>
                  )}
                  <div className="bd-bulk-sep" />
                  <button className="bd-bulk-del" onClick={bulkDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : `Delete ${selected.size}`}
                  </button>
                  <button className="bd-bulk-clear" onClick={() => setSelected(new Set())}>Clear</button>
                </div>
              )}
            </div>
            <table className="bd-table">
              <thead>
                <tr>
                  <th className="bd-th-check">
                    <input type="checkbox" className="bd-check" ref={selectAllRef}
                      checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th onClick={() => sort('name')}>Name <SortArr col="name" /></th>
                  {showCol('position')    && <th onClick={() => sort('position')}>Title <SortArr col="position" /></th>}
                  {showCol('company')     && <th onClick={() => sort('company')}>Company <SortArr col="company" /></th>}
                  {showCol('industry')    && <th onClick={() => sort('industry')}>Industry <SortArr col="industry" /></th>}
                  {showCol('connections') && <th>Attorney Connections</th>}
                  {showCol('email')       && <th>Email</th>}
                </tr>
              </thead>
              <tbody>
                {displayed.map(c => (
                  <tr key={c.id} className={`bd-tr${selected.has(c.id) ? ' bd-tr--sel' : ''}`} onClick={() => setOpen(c)}>
                    <td className="bd-td-check" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="bd-check"
                        checked={selected.has(c.id)}
                        onChange={e => toggleOne(c.id, e)} />
                    </td>
                    <td>
                      <div className="bd-td-name">
                        <div className="bd-av bd-av--sm">
                          {(c.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                          {photoSrc(c) && <img src={photoSrc(c)} alt="" className="bd-av-photo" onError={e => { e.target.style.display = 'none'; }} />}
                        </div>
                        <span className="bd-td-nm">{c.name || '—'}</span>
                      </div>
                    </td>
                    {showCol('position') && <td className="bd-td-dim bd-td-trunc" title={c.position || ''}>{c.position || '—'}</td>}
                    {showCol('company')  && <td className="bd-td-trunc" title={c.company || ''}>{c.company || '—'}</td>}
                    {showCol('industry') && <td className="bd-td-dim bd-td-trunc" title={c.industry || ''}>{c.industry || '—'}</td>}
                    {showCol('connections') && (
                      <td>
                        {(c.connections && c.connections.length) ? (
                          <div className="bd-conn-cell">
                            {c.connections.map(cn => (
                              <span key={cn.attorney_id} className="bd-conn-chip">
                                <span className="bd-conn-atty">{cn.attorney_name || '—'}</span>
                                {cn.relationship_type && (
                                  <span className="bd-conn-rel" style={{ background: relColor(cn.relationship_type) + '22', color: relColor(cn.relationship_type) }}>
                                    {cn.relationship_type}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : <span className="bd-td-dim">—</span>}
                      </td>
                    )}
                    {showCol('email') && (
                      <td className="bd-td-email">
                        {c.work_email
                          ? <a className="bd-td-link" href={`mailto:${c.work_email}`} onClick={e => e.stopPropagation()}>{c.work_email}</a>
                          : <span className="bd-td-dim">—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="bd-empty">No contacts match your filters.</div>
            )}
            <PageSizeBar pageSize={pageSize} setPageSize={setPageSize} shown={displayed.length} total={filtered.length} noun="contacts" />
          </>
        )}
      </div>

      {openContact && (
        <ContactModal
          contact={openContact}
          isAdmin={isAdmin}
          isMythical={currentUser?.isMythical}
          onClose={() => setOpen(null)}
          onUpdate={updated => { setOpen(updated); onRefresh(); }}
          onDelete={() => { setOpen(null); onRefresh(); }}
        />
      )}
      {showAdd && (
        <AddContactModal
          attorneys={attorneys}
          currentUser={currentUser}
          companies={companies}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); onRefresh(); }}
        />
      )}
      {showImport && (
        <ImportModal
          attorneys={attorneys}
          currentUser={currentUser}
          onClose={() => setShowImport(false)}
          onImported={onRefresh}
        />
      )}
    </div>
  );
};


// ── Companies tab ─────────────────────────────────────────────────────────────
const CompaniesView = ({ navCtx = {} }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [sortBy, setSortBy]       = useState('count');
  const [openCompany, setOpenCompany] = useState(navCtx.company || null);

  useEffect(() => {
    axios.get('/api/crm/companies', authH())
      .then(r => setCompanies(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const list = useMemo(() => {
    let arr = [...companies];
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(c => c.company.toLowerCase().includes(q));
    }
    arr.sort((a, b) =>
      sortBy === 'az'         ? a.company.localeCompare(b.company)
    : sortBy === 'count_asc'  ? a.total_contacts - b.total_contacts || a.company.localeCompare(b.company)
    :                           b.total_contacts - a.total_contacts || a.company.localeCompare(b.company));
    return arr;
  }, [companies, search, sortBy]);

  const selected = openCompany ? companies.find(c => c.company === openCompany) : null;

  return (
    <div className="bd-co-wrap">
      <div className="bd-co-list-col">
        <div className="bd-co-controls">
          <div className="bd-search-pill" style={{ maxWidth: '100%' }}>
            <IcoSearch />
            <input className="bd-search" placeholder="Search companies…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="bd-search-x" onClick={() => setSearch('')}>✕</button>}
          </div>
          <select className="bd-ctrl-drop bd-co-sort" value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort companies">
            <option value="count">Most contacts</option>
            <option value="count_asc">Fewest contacts</option>
            <option value="az">Company A–Z</option>
          </select>
        </div>
        <div className="bd-co-list">
          {loading && <p className="bd-empty-sub" style={{ padding: 14 }}>Loading…</p>}
          {!loading && list.length === 0 && <p className="bd-empty">No companies found.</p>}
          {list.map(co => (
            <button
              key={co.company}
              className={`bd-co-row${openCompany === co.company ? ' bd-co-row--on' : ''}`}
              onClick={() => setOpenCompany(co.company === openCompany ? null : co.company)}
            >
              <div className="bd-co-avatar"><IcoBuilding /></div>
              <div className="bd-co-info">
                <span className="bd-co-name">{co.company}</span>
                <span className="bd-co-count">{co.total_contacts} contact{co.total_contacts !== 1 ? 's' : ''}</span>
              </div>
              <span className="bd-co-arrow">›</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bd-co-detail-col">
        {selected ? (
          <div className="bd-co-detail">
            <div className="bd-ml-head">
              <div>
                <h2 className="bd-co-detail-name">{selected.company}</h2>
                <span className="bd-co-contacts-hd">
                  {selected.total_contacts} contact{selected.total_contacts !== 1 ? 's' : ''} · {selected.attorneys.length} attorney{selected.attorneys.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <p className="bd-co-atty-note">Attorneys by contacts at {selected.company}</p>
            <div className="bd-co-atty-list">
              {selected.attorneys.map((a, i) => (
                <div key={a.attorney_id} className="bd-co-atty-row">
                  <span className="bd-co-atty-rank">{i + 1}</span>
                  <div className="bd-co-person-av bd-ml-av">{a.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>
                  <span className="bd-co-atty-name">{a.name}</span>
                  <span className="bd-co-atty-count">{a.count}<span className="bd-co-atty-count-lbl"> contact{a.count !== 1 ? 's' : ''}</span></span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bd-co-empty">
            <div className="bd-co-empty-icon">🏢</div>
            <p>Select a company to see which attorneys have contacts there.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Email tab ─────────────────────────────────────────────────────────────────
const ESBROOK_NAVY = '#272e40';

const LOGO_WHITE = `${window.location.origin}/logos/esbrook_pc_logo_web_no_bg_white.png`;

const buildEmailDoc = (subject, bodyHtml) => `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
  body{margin:0;padding:0;background:#f0f2f5;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
  h1{margin:0 0 8px;font-size:20px;font-weight:600;color:#1e293b;font-family:'Inter',sans-serif;letter-spacing:-0.3px;}
  h2{font-size:15px;font-weight:600;color:#334155;margin:20px 0 6px;font-family:'Inter',sans-serif;}
  p{margin:0 0 14px;font-size:14px;line-height:1.7;color:#475569;font-family:'Inter',sans-serif;}
  img{max-width:100%;height:auto;border-radius:4px;}
</style></head>
<body>
<div style="max-width:600px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,0.08);">
  <div style="background:${ESBROOK_NAVY};padding:24px 40px 22px;">
    <img src="${LOGO_WHITE}" alt="Esbrook P.C." style="height:26px;display:block;margin-bottom:14px;" />
    <div style="color:rgba(255,255,255,0.92);font-size:17px;font-weight:400;letter-spacing:0.1px;font-family:'Inter',-apple-system,sans-serif;">${subject || 'Subject of Email'}</div>
  </div>
  <div style="padding:32px 40px 40px;">
    <p style="margin:0 0 20px;font-size:14px;line-height:1.7;color:#475569;font-family:'Inter',sans-serif;">Hi,</p>
    ${bodyHtml || '<p style="color:#aaa;">Your message will appear here…</p>'}
    <div style="margin-top:32px;padding-top:24px;border-top:1px solid #e2e8f0;">
      <p style="margin:0;font-size:14px;line-height:1.7;color:#475569;font-family:'Inter',sans-serif;">Warm regards,</p>
      <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#1e293b;font-family:'Inter',sans-serif;">Esbrook P.C.</p>
    </div>
  </div>
</div>
</body></html>`;

const EmailView = ({ tags, contacts }) => {
  const [mode, setMode]         = useState('tag');
  const [tagId, setTagId]       = useState('');
  const [subject, setSubject]   = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [status, setStatus]     = useState(null);
  const [sentCount, setSent]    = useState(0);
  const editorRef  = useRef(null);
  const imgInputRef = useRef(null);
  const imgKeyRef  = useRef(0);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const setBlock = (tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
  };

  const handleInsertImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      editorRef.current?.focus();
      document.execCommand('insertImage', false, ev.target.result);
      setBodyHtml(editorRef.current?.innerHTML || '');
    };
    reader.readAsDataURL(file);
    imgKeyRef.current += 1;
  };

  const handleSend = async () => {
    const html = editorRef.current?.innerHTML || '';
    if (!subject.trim() || !html.trim()) return;
    if (mode === 'tag' && !tagId) return;
    setStatus('sending');
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const payload = {
        subject: subject.trim(),
        body: buildEmailDoc(subject.trim(), html),
      };
      if (mode === 'tag') payload.tag_ids = [parseInt(tagId)];
      else payload.contact_ids = contacts.map(c => c.id);
      const res = await axios.post('/api/crm/email/send', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSent(res.data.sent || 0);
      setStatus('sent');
      setSubject('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      setBodyHtml('');
    } catch {
      setStatus('error');
    }
  };

  const selectedTag = tags.find(t => String(t.id) === tagId);
  const recipientCount = mode === 'all'
    ? contacts.filter(c => c.work_email).length
    : (selectedTag ? selectedTag.contact_count : 0);

  const previewDoc = buildEmailDoc(subject, bodyHtml);

  return (
    <div className="bd-email-wrap">

      {/* ── Left: compose panel ── */}
      <div className="bd-email-compose">
        <div className="bd-email-card">
          <div className="bd-email-body">

            {/* Recipients */}
            <div className="bd-field">
              <label className="bd-label">Recipients</label>
              <div className="bd-toggle">
                <button className={`bd-toggle-btn${mode === 'all' ? ' on' : ''}`} onClick={() => setMode('all')}>All Contacts</button>
                <button className={`bd-toggle-btn${mode === 'tag' ? ' on' : ''}`} onClick={() => setMode('tag')}>By Tag</button>
              </div>
              {mode === 'tag' && (
                <select className="bd-email-sel" value={tagId} onChange={e => setTagId(e.target.value)}>
                  <option value="">Select a tag…</option>
                  {tags.map(t => <option key={t.id} value={t.id}>{t.name} ({t.contact_count})</option>)}
                </select>
              )}
              {recipientCount > 0 && (
                <p className="bd-recipient-count">
                  <span className="bd-rc-dot" />
                  {recipientCount.toLocaleString()} recipient{recipientCount !== 1 ? 's' : ''} with email addresses
                </p>
              )}
            </div>

            {/* Subject */}
            <div className="bd-field">
              <label className="bd-label">Subject</label>
              <input className="bd-email-input" placeholder="Email subject…"
                value={subject} onChange={e => setSubject(e.target.value)} />
            </div>

            {/* Toolbar */}
            <div className="bd-field">
              <label className="bd-label">Body</label>
              <div className="bd-editor-toolbar">
                <select className="bd-tb-sel" defaultValue="" onChange={e => { exec('fontName', e.target.value); e.target.value = ''; }}>
                  <option value="" disabled>Font</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="Calibri, sans-serif">Calibri</option>
                  <option value="'Courier New', monospace">Courier New</option>
                </select>
                <select className="bd-tb-sel bd-tb-sel--sm" defaultValue="" onChange={e => { exec('fontSize', e.target.value); e.target.value = ''; }}>
                  <option value="" disabled>Size</option>
                  <option value="1">10</option>
                  <option value="2">12</option>
                  <option value="3">14</option>
                  <option value="4">16</option>
                  <option value="5">18</option>
                  <option value="6">24</option>
                  <option value="7">36</option>
                </select>
                <label className="bd-tb-color" title="Text color">
                  <span className="bd-tb-color-icon">A</span>
                  <input type="color" defaultValue="#000000" onChange={e => exec('foreColor', e.target.value)} />
                </label>
                <div className="bd-tb-sep" />
                <button className="bd-tb-btn bd-tb-h1" onClick={() => setBlock('h1')} title="Heading 1">H1</button>
                <button className="bd-tb-btn bd-tb-h2" onClick={() => setBlock('h2')} title="Heading 2">H2</button>
                <button className="bd-tb-btn" onClick={() => setBlock('p')} title="Body text">¶</button>
                <div className="bd-tb-sep" />
                <button className="bd-tb-btn bd-tb-bold" onClick={() => exec('bold')} title="Bold"><b>B</b></button>
                <button className="bd-tb-btn bd-tb-italic" onClick={() => exec('italic')} title="Italic"><i>I</i></button>
                <button className="bd-tb-btn bd-tb-underline" onClick={() => exec('underline')} title="Underline"><u>U</u></button>
                <div className="bd-tb-sep" />
                <button className="bd-tb-btn" onClick={() => imgInputRef.current?.click()} title="Insert image">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </button>
                <input key={imgKeyRef.current} ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleInsertImage} />
              </div>

              {/* Contenteditable editor */}
              <div
                ref={editorRef}
                className="bd-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={() => setBodyHtml(editorRef.current?.innerHTML || '')}
                data-placeholder="Start typing your message…"
              />
            </div>

            {status === 'sent'  && <div className="bd-status bd-status--ok">Campaign sent to {sentCount.toLocaleString()} recipient{sentCount !== 1 ? 's' : ''}.</div>}
            {status === 'error' && <div className="bd-status bd-status--err">Failed to send. Check recipients and try again.</div>}

            <button
              className="bd-send-btn"
              onClick={handleSend}
              disabled={status === 'sending' || !subject.trim() || !bodyHtml.trim() || (mode === 'tag' && !tagId)}
            >
              {status === 'sending' ? 'Sending…' : `Send Campaign${recipientCount ? ` (${recipientCount})` : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* ── Right: live preview ── */}
      <div className="bd-email-preview-col">
        <p className="bd-label" style={{ marginBottom: 10 }}>Preview</p>
        <div className="bd-email-preview-frame">
          <iframe
            title="Email preview"
            srcDoc={previewDoc}
            className="bd-email-preview-iframe"
            sandbox="allow-same-origin"
          />
        </div>
      </div>

    </div>
  );
};

// Standalone Email page (moved out of the CRM tabs; Mythical-Admin only).
const EmailPage = () => {
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  useEffect(() => {
    const h = authH();
    axios.get('/api/crm/contacts', h).then(r => setContacts(r.data || [])).catch(() => {});
    axios.get('/api/crm/tags', h).then(r => setTags(r.data || [])).catch(() => {});
  }, []);
  return (
    <div className="bd-emailpage">
      <div className="bd-emailpage-head">
        <h1 className="bd-h1">Email <span className="bd-emailpage-badge">Admin only</span></h1>
      </div>
      <div className="bd-emailpage-body">
        <EmailView tags={tags} contacts={contacts} />
      </div>
    </div>
  );
};

// ── Marketing Lists ─────────────────────────────────────────────────────────────
const AddToListModal = ({ contacts, existingIds, onClose, onAdd }) => {
  const [q, setQ]     = useState('');
  const [sel, setSel] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const avail = contacts.filter(c => !existingIds.has(c.id));
  const filtered = q.trim()
    ? avail.filter(c =>
        (c.name || '').toLowerCase().includes(q.toLowerCase()) ||
        (c.company || '').toLowerCase().includes(q.toLowerCase()) ||
        (c.work_email || '').toLowerCase().includes(q.toLowerCase()))
    : avail;
  const toggle = id => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="bd-modal-back" onClick={onClose}>
      <div className="bd-import-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="bd-import-head">
          <h2 className="bd-import-title">Add contacts to list</h2>
          <button className="bd-import-x" onClick={onClose}>✕</button>
        </div>
        <div className="bd-import-body">
          <div className="bd-search-pill" style={{ marginBottom: 12, maxWidth: '100%' }}>
            <IcoSearch />
            <input className="bd-search" placeholder="Search contacts…" value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="bd-ml-pick">
            {filtered.map(c => (
              <label key={c.id} className="bd-ml-pick-row">
                <input type="checkbox" className="bd-check" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="bd-ml-pick-name">{c.name || '—'}</span>
                <span className="bd-ml-pick-co">{c.company || ''}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="bd-empty-sub" style={{ padding: 14 }}>No more contacts to add.</p>}
          </div>
        </div>
        <div className="bd-import-foot">
          <span className="bd-td-dim" style={{ fontSize: 13 }}>{sel.size} selected</span>
          <button className="bd-import-go" disabled={!sel.size || saving}
            onClick={async () => { setSaving(true); await onAdd([...sel]); }}>
            {saving ? 'Adding…' : `Add ${sel.size || ''} contact${sel.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const WinePicker = ({ value, wines = WINE_INFO, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const toggle = e => {
    e.stopPropagation();
    setOpen(o => {
      const next = !o;
      // Flip the menu upward when a bottom-of-screen row wouldn't have room
      // for it below (otherwise it gets clipped by the list container).
      if (next && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        const MENU_H = 220;
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropUp(spaceBelow < MENU_H && rect.top > spaceBelow);
      }
      return next;
    });
  };
  return (
    <div className="bd-wine-pick" ref={ref}>
      <button ref={btnRef} className="bd-wine-pill bd-wine-pill--btn" title={value ? wineLabel(wines, value) : 'Set wine'}
        onClick={toggle}>
        {value || '—'} <span className="bd-wine-caret">▾</span>
      </button>
      {open && (
        <div className={`bd-wine-menu${dropUp ? ' bd-wine-menu--up' : ''}`} onClick={e => e.stopPropagation()}>
          {[1, 2, 3, 4].map(n => (
            <button key={n} className={`bd-wine-opt${value === n ? ' bd-wine-opt--on' : ''}`}
              onClick={() => { onChange(n); setOpen(false); }}>
              <span className="bd-wine-opt-num">{n}</span>
              <span className="bd-wine-opt-name">{wines[n]?.name || ''}</span>
            </button>
          ))}
          <button className="bd-wine-opt bd-wine-opt--clear" onClick={() => { onChange(null); setOpen(false); }}>Clear</button>
        </div>
      )}
    </div>
  );
};

// Edit the four wine selections (name / year / price) for a wine list.
const WineEditor = ({ wines, onClose, onSave }) => {
  const [rows, setRows] = useState(() =>
    [1, 2, 3, 4].map(n => ({ ...(wines[n] || { vintage: '', name: '', price: '' }) })));
  const [saving, setSaving] = useState(false);
  const setField = (i, k, v) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const submit = async () => {
    if (saving) return;
    setSaving(true);
    const obj = {}; rows.forEach((r, i) => { obj[i + 1] = { vintage: r.vintage.trim(), name: r.name.trim(), price: r.price.trim() }; });
    try { await onSave(obj); }
    catch (err) { alert(err.response?.data?.error || 'Could not save wines'); }
    finally { setSaving(false); }
  };
  return (
    <div className="bd-modal-back" onClick={onClose}>
      <div className="bd-listeditor" onClick={e => e.stopPropagation()}>
        <div className="bd-listeditor-head">
          <h3>🍷 Edit Wine Selections</h3>
          <button className="bd-import-x" onClick={onClose}>✕</button>
        </div>
        <div className="bd-listeditor-body">
          {rows.map((r, i) => (
            <div key={i} className="bd-wineedit-row">
              <span className="bd-wineedit-num">{i + 1}</span>
              <input className="bd-le-input bd-wineedit-year"  value={r.vintage} placeholder="Year"  onChange={e => setField(i, 'vintage', e.target.value)} />
              <input className="bd-le-input bd-wineedit-name"  value={r.name}    placeholder="Wine name" onChange={e => setField(i, 'name', e.target.value)} />
              <input className="bd-le-input bd-wineedit-price" value={r.price}   placeholder="$"     onChange={e => setField(i, 'price', e.target.value)} />
            </div>
          ))}
        </div>
        <div className="bd-listeditor-foot">
          <button className="bd-import-back" onClick={onClose}>Cancel</button>
          <button className="bd-import-go" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// Create / edit a marketing list: name, icon, color
const ListEditor = ({ list, onClose, onSave }) => {
  const [name, setName]   = useState(list?.name || '');
  const [icon, setIcon]   = useState(list?.icon || 'stack');
  const [color, setColor] = useState(list?.color || LIST_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try { await onSave({ name: name.trim(), icon, color }); }
    catch (err) { alert(err.response?.data?.error || 'Could not save list'); }
    finally { setSaving(false); }
  };
  return (
    <div className="bd-modal-back" onClick={onClose}>
      <div className="bd-listeditor" onClick={e => e.stopPropagation()}>
        <div className="bd-listeditor-head">
          <h3>{list ? 'Edit List' : 'New List'}</h3>
          <button className="bd-import-x" onClick={onClose}>✕</button>
        </div>
        <div className="bd-listeditor-body">
          <label className="bd-le-label">Name</label>
          <input className="bd-le-input" autoFocus value={name} placeholder="e.g. 2026 Wine List"
            onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} />
          <label className="bd-le-label">Icon</label>
          <div className="bd-le-icons">
            {LIST_ICON_KEYS.map(k => (
              <button key={k} type="button" className={`bd-le-icon${icon === k ? ' on' : ''}`}
                style={icon === k ? { color, borderColor: color } : {}} onClick={() => setIcon(k)}>
                {listIcon(k)}
              </button>
            ))}
          </div>
          <label className="bd-le-label">Color</label>
          <div className="bd-le-colors">
            {LIST_COLORS.map(c => (
              <button key={c} type="button" className={`bd-le-color${color === c ? ' on' : ''}`}
                style={{ background: c }} onClick={() => setColor(c)} aria-label={c} />
            ))}
          </div>
          <div className="bd-le-preview">
            <span className="bd-ml-card-ico" style={{ color, background: color + '18' }}>{listIcon(icon)}</span>
            <span className="bd-ml-card-name">{name || 'List name'}</span>
          </div>
        </div>
        <div className="bd-listeditor-foot">
          <button className="bd-import-back" onClick={onClose}>Cancel</button>
          <button className="bd-import-go" onClick={submit} disabled={!name.trim() || saving}>
            {saving ? 'Saving…' : list ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MarketingLists = ({ contacts, currentUser, onRefresh }) => {
  const [lists, setLists]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [openId, setOpenId]       = useState(null);
  const [members, setMembers]     = useState([]);
  const [openContact, setOpenContact] = useState(null); // opened in-place, stays on the list
  const [memLoading, setMemLoading] = useState(false);
  const [search, setSearch]       = useState('');
  const [editor, setEditor]       = useState(null); // 'new' | list object being edited
  const [ctx, setCtx]             = useState(null); // { list, x, y } right-click menu
  const [showAdd, setShowAdd]     = useState(false);
  const [wineEdit, setWineEdit]   = useState(false);
  const [sortCol, setSortCol]     = useState('name');
  const [sortDir, setSortDir]     = useState('asc');

  const fetchLists = useCallback(async () => {
    try { const { data } = await axios.get('/api/crm/lists', authH()); setLists(data || []); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchLists(); }, [fetchLists]);

  const openList = useCallback(async (id) => {
    setOpenId(id); setMemLoading(true);
    try { const { data } = await axios.get(`/api/crm/lists/${id}/contacts`, authH()); setMembers(data || []); }
    catch { setMembers([]); }
    setMemLoading(false);
  }, []);

  const saveList = async ({ name, icon, color }) => {
    if (editor && editor !== 'new') {
      await axios.patch(`/api/crm/lists/${editor.id}`, { name, icon, color }, authH());
      setEditor(null);
      await fetchLists();
    } else {
      const { data } = await axios.post('/api/crm/lists', { name, icon, color }, authH());
      setEditor(null);
      await fetchLists();
      openList(data.id);
    }
  };

  const duplicateList = async (l) => {
    try {
      const { data } = await axios.post(`/api/crm/lists/${l.id}/duplicate`, {}, authH());
      await fetchLists();
      openList(data.id);
    } catch (err) { alert(err.response?.data?.error || 'Could not duplicate list'); }
  };

  const deleteList = async (id) => {
    if (!window.confirm('Delete this list? The contacts themselves are not deleted.')) return;
    await axios.delete(`/api/crm/lists/${id}`, authH()).catch(() => {});
    if (openId === id) { setOpenId(null); setMembers([]); }
    fetchLists();
  };

  // Close the right-click menu on any outside click / escape
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const key = e => { if (e.key === 'Escape') setCtx(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [ctx]);

  const addContacts = async (ids) => {
    await axios.post(`/api/crm/lists/${openId}/contacts`, { contact_ids: ids }, authH()).catch(() => {});
    setShowAdd(false);
    await openList(openId); fetchLists();
  };

  const removeMember = async (contactId) => {
    await axios.delete(`/api/crm/lists/${openId}/contacts/${contactId}`, authH()).catch(() => {});
    setMembers(m => m.filter(x => x.id !== contactId));
    fetchLists();
  };

  const exportList = () => {
    if (!selected) return;
    // Wine lists get the number and the bottle/gift name as SEPARATE columns
    // (not a "3 — Barolo" mash), pulling names from the list's wine_selections.
    const wineSet = selected.wine_selections || WINE_INFO;
    const wineCols = /wine/i.test(selected.name)
      ? [
          { label: 'Wine #',      val: p => p.wine_tier || '' },
          { label: 'Wine / Gift', val: p => (p.wine_tier ? (wineSet[p.wine_tier]?.name || '') : '') },
        ]
      : [];
    const cols = [
      { key: 'name',             label: 'Name' },
      { key: 'company',          label: 'Company' },
      { key: 'position',         label: 'Title' },
      ...wineCols,
      { key: 'work_address',     label: 'Business Address' },
      { key: 'personal_address', label: 'Personal Address' },
      { key: 'work_email',       label: 'Email' },
    ];
    const esc = v => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const rows = [
      cols.map(c => esc(c.label)).join(','),
      ...members.map(p => cols.map(col => esc(col.val ? col.val(p) : p[col.key])).join(',')),
    ];
    // Lead with a UTF-8 BOM so Excel renders accented names (América, Renée)
    // correctly instead of as garbled symbols.
    const blob = new Blob(['﻿' + rows.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${selected.name}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const updateWine = async (contactId, wine_tier) => {
    const prev = members;
    setMembers(m => m.map(x => x.id === contactId ? { ...x, wine_tier } : x)); // optimistic
    try { await axios.patch(`/api/crm/contacts/${contactId}/wine`, { wine_tier }, authH()); }
    catch (err) { setMembers(prev); alert(err.response?.data?.error || 'Could not update wine'); }
  };

  const selected = lists.find(l => l.id === openId);
  const isWineList = !!selected && /wine/i.test(selected.name);
  const wines = (selected && selected.wine_selections) || WINE_INFO;

  const saveWines = async (obj) => {
    await axios.patch(`/api/crm/lists/${selected.id}`, { wine_selections: obj }, authH());
    setWineEdit(false);
    await fetchLists();
  };
  const memberIds = new Set(members.map(m => m.id));

  const sort = (col) => {
    setSortDir(d => (col === sortCol ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortCol(col);
  };
  const SortMark = ({ col }) => <span className="bd-ml-sortarr">{sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}</span>;

  const shownMembers = useMemo(() => {
    let arr = [...members];
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(m =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.company || '').toLowerCase().includes(q) ||
        (m.work_email || '').toLowerCase().includes(q));
    }
    arr.sort((a, b) => {
      if (sortCol === 'wine_tier') {
        const an = a.wine_tier == null, bn = b.wine_tier == null;
        if (an && bn) return 0;
        if (an) return 1;          // blanks always last
        if (bn) return -1;
        return sortDir === 'asc' ? a.wine_tier - b.wine_tier : b.wine_tier - a.wine_tier;
      }
      const av = (a[sortCol] || '').toLowerCase(), bv = (b[sortCol] || '').toLowerCase();
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [members, search, sortCol, sortDir]);

  return (
    <div className="bd-ml-page">
      {/* Lists as selectable cards across the top */}
      <div className="bd-ml-cards">
        {loading && <p className="bd-empty-sub" style={{ padding: 8 }}>Loading…</p>}
        {!loading && lists.length === 0 && <p className="bd-empty-sub" style={{ padding: 8 }}>No marketing lists yet — create one →</p>}
        {lists.map(l => (
          <button key={l.id} className={`bd-ml-card${openId === l.id ? ' bd-ml-card--on' : ''}`}
            onClick={() => openList(l.id)}
            onContextMenu={e => { e.preventDefault(); setCtx({ list: l, x: e.clientX, y: e.clientY }); }}>
            <span className="bd-ml-card-ico" style={l.color ? { color: l.color, background: l.color + '18' } : {}}>{listIcon(l.icon)}</span>
            <span className="bd-ml-card-body">
              <span className="bd-ml-card-name">{l.name}</span>
              <span className="bd-ml-card-count">{l.contact_count} contact{l.contact_count !== 1 ? 's' : ''}</span>
            </span>
          </button>
        ))}
        <button className="bd-ml-card bd-ml-card--add" onClick={() => setEditor('new')}>+ New List</button>
      </div>

      {/* Selected list → full-width table */}
      {selected ? (
        <div className="bd-ml-detail">
          <div className="bd-ml-detail-head">
            <div className="bd-ml-detail-title">
              <span className="bd-ml-detail-ico" style={selected.color ? { color: selected.color, background: selected.color + '18' } : {}}>{listIcon(selected.icon)}</span>
              <h2 className="bd-ml-detail-name">{selected.name}</h2>
              <span className="bd-ml-detail-count">{selected.contact_count} contact{selected.contact_count !== 1 ? 's' : ''}</span>
            </div>
            <div className="bd-ml-toolbar">
              <div className="bd-search-pill bd-ml-search">
                <IcoSearch />
                <input className="bd-search" placeholder="Search contacts…" value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button className="bd-search-x" onClick={() => setSearch('')}>✕</button>}
              </div>
              <button className="bd-ctrl-btn" onClick={() => setShowAdd(true)}>+ Add contacts</button>
              <button className="bd-ctrl-btn bd-export-btn" disabled={!members.length} onClick={exportList}>↓ Export</button>
            </div>
          </div>

          {isWineList && (
            <div className="bd-wine-key">
              <div className="bd-wine-key-head">
                <span className="bd-wine-key-title">Wine Selections</span>
                <button className="bd-wine-key-edit" onClick={() => setWineEdit(true)}>✎ Edit</button>
              </div>
              <div className="bd-wine-key-grid">
                {[1, 2, 3, 4].map(n => (
                  <div key={n} className="bd-wine-key-item">
                    <span className="bd-wine-key-num">{n}</span>
                    <span className="bd-wine-key-name">
                      {wines[n]?.vintage && <span className="bd-wine-key-vintage">{wines[n].vintage}</span>} {wines[n]?.name || '—'}
                    </span>
                    <span className="bd-wine-key-price">{wines[n]?.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {memLoading ? (
            <p className="bd-empty-sub" style={{ padding: 20 }}>Loading…</p>
          ) : members.length === 0 ? (
            <div className="bd-co-empty" style={{ padding: '36px 0' }}>
              <div className="bd-co-empty-icon">📋</div>
              <p>No contacts yet — use “Add contacts” to build this list.</p>
            </div>
          ) : shownMembers.length === 0 ? (
            <div className="bd-co-empty" style={{ padding: '36px 0' }}>
              <div className="bd-co-empty-icon">🔎</div>
              <p>No contacts match “{search}”.</p>
            </div>
          ) : (
            <div className="bd-ml-table-wrap">
              <table className="bd-ml-table">
                <thead>
                  <tr>
                    <th className="bd-ml-th-av" />
                    <th onClick={() => sort('name')}>Name <SortMark col="name" /></th>
                    <th onClick={() => sort('company')}>Company <SortMark col="company" /></th>
                    {isWineList && <th className="bd-ml-th-wine" onClick={() => sort('wine_tier')}>Wine <SortMark col="wine_tier" /></th>}
                    <th>Email</th>
                    <th className="bd-ml-th-act" />
                  </tr>
                </thead>
                <tbody>
                  {shownMembers.map(p => (
                    <tr key={p.id} className="bd-ml-tr">
                      <td className="bd-ml-td-av">
                        <div className="bd-co-person-av bd-ml-av">
                          {photoSrc(p) && <img src={photoSrc(p)} alt="" className="bd-av-photo" onError={e => { e.target.style.display = 'none'; }} />}
                          {p.name ? p.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                        </div>
                      </td>
                      <td>
                        <button className="bd-ml-name-btn" onClick={() => setOpenContact(contacts.find(c => c.id === p.id) || p)}>{p.name || '—'}</button>
                      </td>
                      <td className="bd-td-trunc" title={p.company || ''}>{p.company || <span className="bd-td-dim">—</span>}</td>
                      {isWineList && (
                        <td className="bd-ml-td-wine">
                          <WinePicker value={p.wine_tier || null} wines={wines} onChange={n => updateWine(p.id, n)} />
                        </td>
                      )}
                      <td className="bd-ml-td-email">
                        {p.work_email
                          ? <a className="bd-td-link" href={`mailto:${p.work_email}`}>{p.work_email}</a>
                          : <span className="bd-td-dim">—</span>}
                      </td>
                      <td className="bd-ml-td-act">
                        <button className="bd-ml-remove" title="Remove from list" onClick={() => removeMember(p.id)}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="bd-co-empty" style={{ marginTop: 24 }}>
          <div className="bd-co-empty-icon">📋</div>
          <p>Select a list above, or create one to email a specific group of contacts.</p>
        </div>
      )}

      {showAdd && selected && (
        <AddToListModal contacts={contacts} existingIds={memberIds}
          onClose={() => setShowAdd(false)} onAdd={addContacts} />
      )}

      {/* Open the contact in place so we stay on the wine list instead of
          jumping to the Contacts tab. */}
      {openContact && (
        <ContactModal
          contact={openContact}
          isAdmin={currentUser?.isAdmin}
          isMythical={currentUser?.isMythical}
          onClose={() => setOpenContact(null)}
          onUpdate={u => { setOpenContact(u); onRefresh?.(); if (openId) openList(openId); }}
          onDelete={() => { setOpenContact(null); onRefresh?.(); if (openId) openList(openId); }}
        />
      )}

      {ctx && (
        <div className="bd-ctx-menu" style={{ top: ctx.y, left: ctx.x }}
          onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          <button onClick={() => { setEditor(ctx.list); setCtx(null); }}>Edit</button>
          <button onClick={() => { duplicateList(ctx.list); setCtx(null); }}>Duplicate</button>
          <button className="bd-ctx-danger" onClick={() => { const id = ctx.list.id; setCtx(null); deleteList(id); }}>Delete</button>
        </div>
      )}

      {editor && (
        <ListEditor list={editor === 'new' ? null : editor} onClose={() => setEditor(null)} onSave={saveList} />
      )}

      {wineEdit && selected && (
        <WineEditor wines={wines} onClose={() => setWineEdit(false)} onSave={saveWines} />
      )}
    </div>
  );
};

// ── Audit log (Logs tab) ────────────────────────────────────────────────────────
const ACTION_META = {
  create:            { label: 'Created',    color: '#16a34a' },
  update:            { label: 'Edited',     color: '#2563eb' },
  enrich:            { label: 'Enriched',   color: '#7c3aed' },
  delete:            { label: 'Deleted',    color: '#dc2626' },
  bulk_delete:       { label: 'Bulk delete',color: '#dc2626' },
  share:             { label: 'Visibility', color: '#0891b2' },
  bulk_share:        { label: 'Visibility', color: '#0891b2' },
  bulk_settype:      { label: 'Bulk edit',  color: '#2563eb' },
  tag_add:           { label: 'Tag',        color: '#d97706' },
  tag_remove:        { label: 'Tag',        color: '#d97706' },
  connection_set:    { label: 'Connection', color: '#4f46e5' },
  connection_remove: { label: 'Connection', color: '#4f46e5' },
  revert:            { label: 'Reverted',   color: '#64748b' },
};

const AuditLog = () => {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [reverting, setReverting] = useState(null);

  const fetchLog = useCallback(async () => {
    try { const { data } = await axios.get('/api/crm/audit', authH()); setEntries(data || []); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  const revert = async (id) => {
    if (!window.confirm('Undo this change? This will restore what it was before.')) return;
    setReverting(id);
    try { await axios.post(`/api/crm/audit/${id}/revert`, {}, authH()); await fetchLog(); }
    catch (err) { alert(err.response?.data?.error || 'Could not revert this change'); }
    finally { setReverting(null); }
  };

  const fmt = ts => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="bd-log">
      <div className="bd-log-head">
        <h3 className="bd-card-title" style={{ margin: 0 }}>Change Log</h3>
        <span className="bd-empty-sub">Every edit to a contact — who made it, when, and what changed. Revert any of them.</span>
      </div>
      {loading ? (
        <div className="bd-loading"><div className="bd-spinner" /></div>
      ) : entries.length === 0 ? (
        <p className="bd-empty">No changes recorded yet.</p>
      ) : (
        <div className="bd-log-list">
          {entries.map(e => {
            const m = ACTION_META[e.action] || { label: e.action, color: '#64748b' };
            const canRevert = e.action !== 'revert' && !e.reverted;
            return (
              <div key={e.id} className={`bd-log-row${e.reverted ? ' bd-log-row--reverted' : ''}`}>
                <span className="bd-log-badge" style={{ color: m.color, background: m.color + '18' }}>{m.label}</span>
                <div className="bd-log-body">
                  <span className="bd-log-summary">
                    {e.summary || m.label}{e.entity_name ? <> · <b>{e.entity_name}</b></> : null}
                  </span>
                  <span className="bd-log-meta">{e.actor_name || 'Unknown'} · {fmt(e.created_at)}{e.reverted ? ' · reverted' : ''}</span>
                </div>
                {canRevert
                  ? <button className="bd-log-revert" disabled={reverting === e.id} onClick={() => revert(e.id)}>
                      {reverting === e.id ? 'Undoing…' : 'Revert'}
                    </button>
                  : e.reverted ? <span className="bd-log-reverted-tag">Reverted</span> : <span />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── BD Hub shell ──────────────────────────────────────────────────────────────
const BDHub = ({ currentUser }) => {
  const [tab, setTab]           = useState('contacts');
  const [navCtx, setNavCtx]     = useState({});   // context consumed on tab mount
  const [contacts, setContacts] = useState([]);
  const [tags, setTags]         = useState([]);
  const [attorneys, setAtty]    = useState([]);
  const [loading, setLoading]   = useState(true);

  // Navigate to a tab, optionally with context that views read on mount
  const navigate = useCallback((tabId, ctx = {}) => {
    setNavCtx(ctx);
    setTab(tabId);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const h = authH();
      const [c, t, a] = await Promise.all([
        axios.get('/api/crm/contacts', h),
        axios.get('/api/crm/tags', h),
        axios.get('/api/crm/staff', h),
      ]);
      setContacts(c.data || []);
      setTags(t.data || []);
      setAtty(a.data || []);
    } catch {}
    setLoading(false);
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/crm/tags', authH());
      setTags(data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="bd-hub">
      <aside className="bd-sidebar">
        <div className="bd-nav-card">
          <div className="bd-brand">
            <p className="bd-brand-name">{currentUser?.isAdmin ? 'Firm CRM' : 'My CRM'}</p>
          </div>

          <nav className="bd-nav">
            {TABS.map(t => (
              <button key={t.id}
                className={`bd-nav-item${tab === t.id ? ' on' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.icon}
                <span>{t.id === 'companies' ? 'Firm Connections (Companies)' : t.label}</span>
              </button>
            ))}
          </nav>
        </div>

      </aside>

      <main className="bd-main">
        <header className="bd-header">
          <div>
            <h1 className="bd-h1">{tab === 'companies' ? 'Firm Connections (Companies)' : TABS.find(t => t.id === tab)?.label}</h1>
          </div>
        </header>

        <div className="bd-body">
          {tab === 'dashboard' && <Dashboard contacts={contacts} loading={loading} onNavigate={navigate} />}
          {tab === 'contacts'  && <ContactsView currentUser={currentUser} contacts={contacts} tags={tags} attorneys={attorneys} loading={loading} onRefresh={fetchAll} onTagCreated={fetchTags} navCtx={navCtx} />}
          {tab === 'companies' && <CompaniesView navCtx={navCtx} />}
          {tab === 'lists'     && <MarketingLists contacts={contacts} currentUser={currentUser} onRefresh={fetchAll} />}
          {tab === 'logs'      && <AuditLog />}
        </div>
      </main>
    </div>
  );
};

export default BDHub;
export { EmailPage };
